/**
 * ============================================================================
 * SOURCE OF TRUTH for kernel output types.
 * ============================================================================
 *
 * Type definitions flow TS → Python (codegen); runtime values flow Python → TS
 * (the kernel produces these outputs and the server/client consume them).
 *
 * Zod schemas in this file drive Python `TypedDict` generation. The pipeline:
 *
 *   outputs.ts (Zod)
 *     → zodToJsonSchema (packages/common/scripts/kernel-output-types-generate-schemas.ts)
 *     → packages/common/src/kernel-output-types/generated/kernel-output-types.schema.json
 *     → datamodel-codegen
 *     → python/common/src/hex/generated/kernel_output_types.py
 *       (TypedDicts, importable as `hex.generated.kernel_output_types`)
 *
 * After editing any schema here, run `hdev gen:kernel-output-types` to refresh
 * both the JSON Schema and the generated Python file. CircleCI's
 * `gen-kernel-output-types` job enforces this — CI fails if the generated
 * files are stale.
 *
 * Consumers:
 *   - TS runtime validation: `import { KernelOutputTypes } from "@hex/common"`
 *     then `KernelOutputTypes.TableStoreExportContents.parse(...)` etc.
 *   - Python producers: `from hex.generated.kernel_output_types import DisplayTableOutput`
 *     etc., then annotate dict literals.
 *
 * Do NOT edit `generated/kernel-output-types.schema.json` or
 * `python/common/src/hex/generated/kernel_output_types.py` by hand — they are
 * regenerated from this file.
 * ============================================================================
 */

import { z } from "zod";

import { DataConnectionId, DisplayTableColumnId } from "../idTypeBrands.js";

/**
 * Reference to a single TableStore entry. Produced by kernel-side code
 * (python/hisc, python-kernel-startup) and consumed by the server and client.
 */
export const TableStoreReference = z.object({
  contentHash: z.string(),
  versionId: z.string(),
  persistence: z.enum(["s3", "local"]).optional(),
});
export type TableStoreReference = z.infer<typeof TableStoreReference>;

/**
 * HQL metadata attached to compiled SQL queries for debugging/inspection.
 */
export const HqlNextOutput = z.object({
  query: z.string(),
  datasets: z.string(),
  name: z.enum([
    "explore_chart",
    "explore_table",
    "explore_pivot",
    "explore_table_aggregation",
    "explore_table_page",
  ]),
  temporaryParamMapping: z.string(),
});
export type HqlNextOutput = z.infer<typeof HqlNextOutput>;

/**
 * Wire shape for a compiled SQL query displayed alongside query-mode outputs.
 * Produced by Python (hisc.output_sql, data-service-python.prepare_compiled_query_output).
 */
export const SqlCompiledQuery = z.object({
  query: z.string(),
  queryLength: z.number(),
  parameters: z.array(z.unknown()),
  parametersLength: z.number(),
  hqlInfo: HqlNextOutput.optional(),
});
export type SqlCompiledQuery = z.infer<typeof SqlCompiledQuery>;

/**
 * Wire shape for query-mode CSV download outputs. Produced by
 * hisc.export_warehouse_query_for_csv_download.
 */
export const QueryModeCsvDownloadContents = z.object({
  query: z.string(),
  parameters: z.array(z.unknown()),
  connectionId: DataConnectionId,
  exportLabel: z.string().optional(),
});
export type QueryModeCsvDownloadContents = z.infer<
  typeof QueryModeCsvDownloadContents
>;

/**
 * Common fields across all table-store-based export variants.
 */
export const BaseTableStoreExportContents = z.object({
  sql: z.string(),
  parameters: z.array(z.unknown()),
  columnIds: z.array(z.string()),
  columnRenames: z.record(z.string(), z.string()).nullable(),
  exportLabel: z.string().optional(),
});
export type BaseTableStoreExportContents = z.infer<
  typeof BaseTableStoreExportContents
>;

/**
 * Single-entry table-store export (references one content-hash).
 */
export const SingleTableStoreExportContents =
  BaseTableStoreExportContents.extend({
    type: z.literal("tablestore"),
    contentHash: z.string(),
    versionId: z.string(),
    sourceName: z.string(),
  });
export type SingleTableStoreExportContents = z.infer<
  typeof SingleTableStoreExportContents
>;

/**
 * Multi-entry table-store export (references multiple entries by content hash).
 */
export const MultiTableStoreExportContents =
  BaseTableStoreExportContents.extend({
    type: z.literal("tablestore-multi"),
    refs: z.array(TableStoreReference),
  });
export type MultiTableStoreExportContents = z.infer<
  typeof MultiTableStoreExportContents
>;

export const TableStoreExportContents = z.discriminatedUnion("type", [
  SingleTableStoreExportContents,
  MultiTableStoreExportContents,
]);
export type TableStoreExportContents = z.infer<typeof TableStoreExportContents>;

// Export success/failure — kept here to preserve the ExportOutputContents union
// that currently lives in mimeTypes.ts. These are not kernel outputs themselves
// but are part of the same runtypes union.

export const ExportOutputContentsSuccess = z.object({
  success: z.literal(true),
  exportKey: z.string(),
  exportLabel: z.string().optional(),
});
export type ExportOutputContentsSuccess = z.infer<
  typeof ExportOutputContentsSuccess
>;

export const ExportOutputContentsFailure = z.object({
  success: z.literal(false),
  reason: z.string(),
});
export type ExportOutputContentsFailure = z.infer<
  typeof ExportOutputContentsFailure
>;

/**
 * Structured per-stage error entry the kernel emits via the
 * EXECUTION_LOG mimetype. Reports events that occurred during cell
 * execution stages (e.g., overlength arrow table, CSV upload failure)
 * to the user or agent. `context` identifies the stage; `level`
 * indicates severity; `extra` carries diagnostic key-value pairs.
 */
export const ExecutionLogEntry = z.object({
  context: z.string(),
  error: z.string(),
  level: z.enum(["info", "warn", "error"]),
  extra: z.record(z.unknown()).optional(),
});
export type ExecutionLogEntry = z.infer<typeof ExecutionLogEntry>;

export const ExecutionLogPayload = z.object({
  errors: z.array(ExecutionLogEntry),
});
export type ExecutionLogPayload = z.infer<typeof ExecutionLogPayload>;

export const ExportOutputContentsNew = z.discriminatedUnion("success", [
  ExportOutputContentsSuccess,
  ExportOutputContentsFailure,
]);
export type ExportOutputContentsNew = z.infer<typeof ExportOutputContentsNew>;

export const ExportOutputContentsLegacy = z.string();
export type ExportOutputContentsLegacy = z.infer<
  typeof ExportOutputContentsLegacy
>;

export const ExportOutputContents = z.union([
  ExportOutputContentsLegacy,
  ExportOutputContentsNew,
  TableStoreExportContents,
]);
export type ExportOutputContents = z.infer<typeof ExportOutputContents>;

export const SortDirection = z.enum(["ASC", "DESC"]);
export type SortDirection = z.infer<typeof SortDirection>;

/**
 * Column types supported by the display-table output. Mirrors
 * FilledDynamicValueTableColumnTypeLiteral from DynamicValue.ts.
 */
export const DisplayTableColumnOutputType = z.enum([
  "STRING",
  "NUMBER",
  "DATETIME",
  "DATETIMETZ",
  "DATE",
  "TIME",
  "UNKNOWN",
  "BOOLEAN",
]);
export type DisplayTableColumnOutputType = z.infer<
  typeof DisplayTableColumnOutputType
>;

export const DisplayTableExploreOutputType = z.enum([
  "source",
  "pivot",
  "aggregated",
]);
export type DisplayTableExploreOutputType = z.infer<
  typeof DisplayTableExploreOutputType
>;

export const DisplayTableOutputColumn = z.object({
  columnId: DisplayTableColumnId,
  columnName: z.array(z.string()),
  columnType: DisplayTableColumnOutputType,
  columnData: z.array(z.union([z.string(), z.null(), z.undefined()])),
  // Legacy fields preserved for integration-test expectations and the
  // arrow-table output path (packages/common/src/arrow/arrowTableOutputsUtils.ts).
  // Only emitted for single-level column names, not multi-level tuples.
  columnNameDefault: z.string().optional(),
  columnNameOverride: z.string().optional(),
});
export type DisplayTableOutputColumn = z.infer<typeof DisplayTableOutputColumn>;

export const DisplayTableWarningType = z.enum([
  "CONDITIONAL_FORMATTING_ERROR",
  "ORDER_NOT_PRESERVED",
]);
export type DisplayTableWarningType = z.infer<typeof DisplayTableWarningType>;

export const DisplayTableWarning = z.object({
  type: DisplayTableWarningType,
  message: z.string(),
});
export type DisplayTableWarning = z.infer<typeof DisplayTableWarning>;

/**
 * Aggregation function applied to a column. Mirrors
 * HqlAggregationFunctionLiteral in packages/common/src/hql/types.ts.
 */
export const HqlAggregationFunction = z.enum([
  "Min",
  "Max",
  "Avg",
  "Count",
  "CountDistinct",
  "Sum",
  "StdDev",
  "StdDevPop",
  "Variance",
  "VariancePop",
  "Median",
]);
export type HqlAggregationFunction = z.infer<typeof HqlAggregationFunction>;

const ColumnAggregation = z.object({
  result: z.union([z.number(), z.string()]),
  func: HqlAggregationFunction,
});
// Record key is branded DisplayTableColumnId. Zod's z.record requires a
// ZodString/ZodEnum key, so we assert the type after construction.
export const ColumnAggregationsOutput = z.record(
  z.string(),
  ColumnAggregation,
) as unknown as z.ZodType<
  Record<DisplayTableColumnId, z.infer<typeof ColumnAggregation>>
>;
export type ColumnAggregationsOutput = z.infer<typeof ColumnAggregationsOutput>;

/**
 * Display-table output as emitted by python-kernel-startup's display_table_json.
 *
 * calcColumns and conditionalFormatting are kept opaque (passthrough) at this
 * layer — their full definitions live in packages/common/src/display-table
 * and are not needed for kernel output typing (the kernel forwards them).
 */
export const DisplayTableOutput = z.object({
  rowCount: z.number(),
  columns: z.array(DisplayTableOutputColumn),
  columnLevelNames: z.array(z.union([z.string(), z.null()])),
  indices: z
    .array(
      z.union([
        z.string(),
        z.number(),
        z.array(z.union([z.string(), z.number()])),
      ]),
    )
    .optional(),
  indexLevelNames: z.array(z.union([z.string(), z.null()])),
  pageNumberOverride: z.union([z.number(), z.null()]).optional(),
  pageSize: z.number(),

  calcColumns: z.array(z.record(z.any())).nullable().optional(),
  conditionalFormatting: z.record(z.any()).nullable().optional(),
  configId: z.string().nullable().optional(),
  dfSize: z.number().optional(),
  rowCountBeforeTruncation: z.number().optional(),
  rowCountOfPreview: z.boolean().optional(),
  queryMode: z.boolean().optional(),
  warnings: z.array(DisplayTableWarning).nullable().optional(),
  sqlErrorFallback: z.boolean().optional(),
  columnAggregations: ColumnAggregationsOutput.optional(),
  exploreOutputType: DisplayTableExploreOutputType.nullable().optional(),
});
export type DisplayTableOutput = z.infer<typeof DisplayTableOutput>;
